import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./UserAccountRequestListFacets.module.styl";

export const DEFAULT_USER_ACCOUNT_REQUEST_LIST_FILTERS = Object.freeze({
  requestStatus: []
});

const STATUS_OPTIONS = [
  { value: "PENDING", label: t("common.pending") },
  { value: "READY", label: t("common.ready") },
  { value: "ERROR", label: t("common.error") },
  { value: "APPROVED", label: t("common.approved") },
  { value: "REJECTED", label: t("common.rejected") }
];

const normalizeArray = (value) => Array.isArray(value)
  ? value.map((item) => String(item || "").trim()).filter(Boolean)
  : [];

export const normalizeUserAccountRequestListFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const allowed = new Set(STATUS_OPTIONS.map((item) => item.value));

  return {
    requestStatus: normalizeArray(input.requestStatus).map((item) => item.toUpperCase()).filter((item) => allowed.has(item))
  };
};

export const isDefaultUserAccountRequestListFilters = (value) => normalizeUserAccountRequestListFilters(value).requestStatus.length === 0;
export const getUserAccountRequestListActiveFilterCount = (value) => normalizeUserAccountRequestListFilters(value).requestStatus.length;

const quoteRsql = (value) => `"${String(value || "").replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

export const buildUserAccountRequestFilterRsql = (value) => {
  const filters = normalizeUserAccountRequestListFilters(value);

  if (!filters.requestStatus.length) {
    return "";
  }

  return `requestStatus=in=(${filters.requestStatus.map(quoteRsql).join(",")})`;
};

export const combineUserAccountRequestListRsql = (...queries) => queries.map((query) => String(query || "").trim()).filter(Boolean).join(";");

export const loadUserAccountRequestFacetCounts = async ({ search, filters }) => {
  return await rest.request(getServiceUri() + "user-account-request-list/facets", "POST", {
    search: String(search || "").trim(),
    filters: normalizeUserAccountRequestListFilters(filters)
  });
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};
  for (const key of path) current = current?.[key];
  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const FacetSection = ({ title, children }) => <section className={styles.section}><div className={styles.sectionTitle}>{title}</div><div className={styles.sectionBody}>{children}</div></section>;
const CheckboxOption = ({ label, selected, count, onClick }) => <button type="button" className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option} onClick={onClick}><span className={styles.checkBox}>{selected ? "✓" : ""}</span><span className={styles.optionLabel}>{label}</span><span className={styles.count}>{count}</span></button>;
const Chip = ({ label, onRemove }) => <button type="button" className={styles.chip} onClick={onRemove}><span>{label}</span><span className={styles.chipClose}>×</span></button>;

export const UserAccountRequestListFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;
  return <button type="button" className={activeCount ? `${styles.filterButton} ${styles.filterButtonActive}` : styles.filterButton} onClick={controller.toggleDrawer}><span className={styles.filterButtonIcon}><TuneIcon fontSize="inherit" /></span><span className={styles.filterButtonLabel}>{t("common.filters")}</span>{activeCount ? <span className={styles.filterButtonBadge}>{activeCount}</span> : null}</button>;
};

export const UserAccountRequestListFacetChips = ({ controller }) => {
  const filters = normalizeUserAccountRequestListFilters(controller?.value);
  if (isDefaultUserAccountRequestListFilters(filters)) return null;
  return <div className={styles.chipsBar}>{filters.requestStatus.map((value) => <Chip key={value} label={`${t("common.status")}: ${STATUS_OPTIONS.find((item) => item.value === value)?.label || value}`} onRemove={() => controller.patchValue({ requestStatus: filters.requestStatus.filter((item) => item !== value) })} />)}<button type="button" className={styles.clearAll} onClick={controller.reset}>{t("common.clearAll")}</button></div>;
};

const UserAccountRequestListFacets = ({ controller }) => {
  const filters = normalizeUserAccountRequestListFilters(controller?.value);
  const counts = controller?.counts || {};
  return <div className={styles.drawer}><div className={styles.drawerHeader}><div><div className={styles.drawerTitle}>{t("common.filters")}</div><div className={styles.drawerSubtitle}>{controller.countsLoading ? t("common.updatingCounts") : t("filter.refineOperatorRequests")}</div></div><button type="button" className={styles.closeButton} onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}><CloseIcon fontSize="inherit" /></button></div><div className={styles.drawerBody}><FacetSection title={t("common.status")}>{STATUS_OPTIONS.map((option) => <CheckboxOption key={option.value} label={option.label} selected={filters.requestStatus.includes(option.value)} count={countFor(counts, ["status", option.value])} onClick={() => controller.patchValue({ requestStatus: toggleArrayValue(filters.requestStatus, option.value) })} />)}</FacetSection></div><div className={styles.drawerFooter}><button type="button" className={styles.secondaryAction} onClick={controller.reset} disabled={controller.activeCount === 0}><RestartAltIcon fontSize="inherit" /><span>{t("common.resetFilters")}</span></button><button type="button" className={styles.primaryAction} onClick={controller.closeDrawer}>{t("common.done")}</button></div></div>;
};

export const USER_ACCOUNT_REQUEST_LIST_FACETS = {
  storageKey: "haiti.user-account-request.facets.v1",
  overlayLabel: t("filter.operatorRequestFilters"),
  defaultValue: DEFAULT_USER_ACCOUNT_REQUEST_LIST_FILTERS,
  normalizeValue: normalizeUserAccountRequestListFilters,
  isDefaultValue: isDefaultUserAccountRequestListFilters,
  getActiveCount: getUserAccountRequestListActiveFilterCount,
  loadCounts: loadUserAccountRequestFacetCounts,
  renderButton: (controller) => <UserAccountRequestListFacetButton controller={controller} />,
  renderDrawer: (controller) => <UserAccountRequestListFacets controller={controller} />,
  renderChips: (controller) => <UserAccountRequestListFacetChips controller={controller} />
};

export default UserAccountRequestListFacets;
